import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Invitation, Me } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { useRefresh, useRoles, useUsers, wsKey } from "../directory";
import { ErrorBox, Field, InviteSent, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, When, when } from "../ui";
import { HistoryLink } from "../activity";
import { askText, confirmAction } from "../confirm";

function Invitations({ slug, adding, setAdding }: { slug: string; adding: boolean; setAdding: (v: boolean) => void }) {
  const refresh = useRefresh(slug);
  const list = useQuery({ queryKey: wsKey(slug, "invitations"), queryFn: async () => z.array(Invitation).parse(await get(`/v1/ws/${slug}/invitations`, { all: "true" })) });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "guest" | "admin">("member");
  const [sent, setSent] = useState<string>();
  const invite = useMutation({
    mutationFn: () => checkedWrite("POST", `/v1/ws/${slug}/invitations`, { email, role: role === "guest" ? "guest" : "member", systemAdmin: role === "admin" }),
    onSuccess: () => {
      setSent(email);
      setEmail("");
      setAdding(false);
      refresh();
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => api("DELETE", `/v1/ws/${slug}/invitations/${id}`), onSuccess: refresh });
  const shown = (list.data ?? []).filter((i) => i.status !== "accepted");
  return (
    <>
      <SidePanel title="Invite people" open={adding} onClose={() => setAdding(false)} onSubmit={() => invite.mutate()} submitLabel="Send invitation" busy={invite.isPending} error={invite.error}>
        <p className="muted small">They join when they first sign in with this email. Invitations last 14 days.</p>
        <Field label="Email">
          <input type="email" required value={email} autoFocus onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        </Field>
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            <option value="member">Member: works on what they are given access to</option>
            <option value="guest">Guest: reads and comments only</option>
            <option value="admin">Member and system admin</option>
          </select>
        </Field>
      </SidePanel>
    {sent && <InviteSent email={sent} onDone={() => setSent(undefined)} />}
    <Panel title="Invitations" plain>
      <ErrorBox error={revoke.error ?? list.error} />
      {shown.length > 0 && (
        <table className="grid">
          <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.id}>
                <td>{i.email}</td>
                <td>{i.systemAdmin ? `${i.role} + system admin` : i.role}</td>
                <td><StatusBadge status={i.status} /></td>
                <td><When at={i.expiresAt} /></td>
                <td className="row-actions">{i.status === "open" && <RowMenu label={`Actions for ${i.email}`} items={[{ label: "Revoke…", danger: true, onSelect: () => void confirmAction({ title: `Revoke the invitation for ${i.email}?`, body: "They can no longer join with it. You can invite them again.", action: "Revoke", check: () => api("DELETE", `/v1/ws/${slug}/invitations/${i.id}`, { dryRun: true }) }).then((ok) => ok && revoke.mutate(i.id)) }]} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {shown.length === 0 && <p className="muted small">No open invitations.</p>}
    </Panel>
    </>
  );
}

export function UsersPage() {
  const { slug = "" } = useParams();
  const users = useUsers(slug);
  const roles = useRoles(slug);
  const refresh = useRefresh(slug);
  const me = useQuery({ queryKey: ["ws-me", slug], queryFn: async () => Me.parse(await get(`/v1/ws/${slug}/me`)) });
  const myId = me.data?.user?.id;
  const [inviting, setInviting] = useState(false);
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { active?: boolean; displayName?: string; baseRole?: "member" | "guest" } }) => checkedWrite("PATCH", `/v1/ws/${slug}/users/${id}`, body),
    onSuccess: refresh,
  });
  const workspaceRoles = (id: number) =>
    (roles.data ?? []).filter((r) => r.principal.kind === "user" && r.principal.id === id && r.scopeKind === "workspace").map((r) => r.role.replace("_", " "));
  return (
    <>
      <PageHeader title="Users" description="People in this workspace, and invitations not yet taken up." actions={<><HistoryLink slug={slug} kind="users" /><button onClick={() => setInviting(true)}>Invite people</button></>} />
      <Invitations slug={slug} adding={inviting} setAdding={setInviting} />
      <Panel title="Members" plain>
        <ErrorBox error={users.error ?? update.error} />
        <table className="grid">
          <thead><tr><th>Name</th><th>Workspace roles</th><th>Status</th><th className="opt">Joined</th><th /></tr></thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.id} className={u.active ? "" : "inactive"}>
                <td>
                  <div className="who-cell">{u.displayName}</div>
                  <div className="muted small">{u.email}</div>
                </td>
                <td>{workspaceRoles(u.id).map((r) => <span key={r} className="badge">{r}</span>)}</td>
                <td><StatusBadge status={u.active ? "active" : "inactive"} /></td>
                <td className="opt"><When at={u.createdAt} /></td>
                <td className="row-actions">
                  {u.id === myId ? (
                    <span className="muted small">you</span>
                  ) : (
                    <RowMenu
                      label={`Actions for ${u.displayName}`}
                      items={[
                        { label: "Rename…", onSelect: () => void askText({ title: `Rename ${u.displayName}`, action: "Rename", input: { label: "Display name", value: u.displayName } }).then((n) => n && update.mutate({ id: u.id, body: { displayName: n } })) },
                        u.active && workspaceRoles(u.id).includes("guest") && { label: "Make member", onSelect: () => update.mutate({ id: u.id, body: { baseRole: "member" } }) },
                        u.active && !workspaceRoles(u.id).includes("guest") && {
                          label: "Make guest…",
                          onSelect: () => void confirmAction({ title: `Make ${u.displayName} a guest?`, body: "Guests can only read and comment, whatever they are granted. You can make them a member again.", action: "Make guest", danger: false, check: () => api("PATCH", `/v1/ws/${slug}/users/${u.id}`, { body: { baseRole: "guest" }, dryRun: true }) }).then((ok) => ok && update.mutate({ id: u.id, body: { baseRole: "guest" } })),
                        },
                        !u.active && { label: "Reactivate", onSelect: () => update.mutate({ id: u.id, body: { active: true } }) },
                        u.active && {
                          label: "Deactivate…",
                          danger: true,
                          onSelect: () => void confirmAction({ title: `Deactivate ${u.displayName}?`, body: "Their access stops at once. Their history stays, and you can reactivate them.", action: "Deactivate", check: () => api("PATCH", `/v1/ws/${slug}/users/${u.id}`, { body: { active: false }, dryRun: true }) }).then((ok) => ok && update.mutate({ id: u.id, body: { active: false } })),
                        },
                      ]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </>
  );
}
